/**
 * Centralized, type-safe environment variables for the Vite admin app.
 *
 * This is the ONLY file in the admin that reads `import.meta.env`
 * directly. Every other module must import the typed `env` object from
 * here so:
 *   - missing or malformed values fail loudly at startup
 *   - the rest of the codebase can rely on `string` (not `string | undefined`)
 *   - Vite-only flags (`DEV`, `PROD`, `MODE`) are normalised to booleans
 *
 * ## Vite contract
 *
 * Vite exposes variables prefixed with `VITE_` on `import.meta.env` in
 * both the browser bundle and (when imported from `vite.config.ts`) the
 * build script. Variables without that prefix are NOT available to the
 * client. The schema below only declares fields that should be reachable
 * from runtime code; add a new `VITE_*` variable here whenever you need
 * a new value in the admin.
 */
import { z } from 'zod';

// ---------- Schema ----------------------------------------------------------

const schema = z.object({
  // Medusa backend URL (browsable from the admin app's network)
  VITE_MEDUSA_BACKEND_URL: z
    .string()
    .url({ message: 'VITE_MEDUSA_BACKEND_URL must be a valid URL' })
    .default('http://localhost:9000'),

  // Vite build/runtime flags. `MODE` is the value of `import.meta.env.MODE`
  // (matches `--mode` flag or `mode` field in vite.config).
  MODE: z.enum(['development', 'test', 'production']).default('development'),
});

// ---------- Parse & validate -----------------------------------------------

// Vite exposes VITE_* env variables on `import.meta.env`. We explicitly
// map them here to ensure Vite's static replacement works reliably
// across different build configurations and environments.
const parsed = schema.safeParse({
  VITE_MEDUSA_BACKEND_URL: import.meta.env.VITE_MEDUSA_BACKEND_URL,
  MODE: import.meta.env.MODE,
});

if (!parsed.success) {
  const formatted = parsed.error.issues
    .map((issue) => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`)
    .join('\n');
  throw new Error(
    `[env] Invalid admin environment variables:\n${formatted}\n\n` +
      `Fix the values in apps/admin/.env (or apps/admin/.env.local).`
  );
}

// ---------- Exported env object --------------------------------------------

/**
 * Frozen, fully-typed environment object. Import this anywhere instead
 * of touching `import.meta.env` directly.
 */
export const env = Object.freeze({
  VITE_MEDUSA_BACKEND_URL: parsed.data.VITE_MEDUSA_BACKEND_URL,
  MODE: parsed.data.MODE,
  // `DEV`/`PROD` are exposed by Vite at build time and are the canonical
  // source of truth for "are we in a dev build". Mirror them here so the
  // rest of the codebase never needs `import.meta.env`.
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
  isTest: parsed.data.MODE === 'test',
});

export type Env = typeof env;
